/**
 * Guessing an ONU's model from the only thing the chassis tells us about it.
 *
 * An unconfigured ONU reports a PON port, a serial and a state — no model. But
 * the serial is not opaque: its first four characters are the vendor's own
 * code, assigned by the ITU, and the remaining eight are that vendor's serial.
 * `HWTC3667930C` is a Huawei; `ZTEGC6A2955E` is a ZTE. That is enough to narrow
 * a library of models down to one vendor's, and usually to one obvious answer.
 *
 * It is a suggestion, never a decision. Where the guess is weak the operator is
 * asked, because the ONU type decides which commands the chassis will accept
 * for that subscriber and a wrong one produces a config it quietly will not
 * apply.
 */

/**
 * ITU-T ONU vendor IDs → the vendor that registered them.
 *
 * The first four characters of a GPON serial are an ITU-T-assigned vendor code,
 * so this table is how a bare, unconfigured ONU gets a vendor before anyone has
 * told the platform what it is. Only codes whose owner is KNOWN belong here — a
 * wrong mapping suggests a model from the wrong vendor and produces a config the
 * chassis quietly refuses, which is worse than admitting the code is unknown and
 * asking the operator to pick. An unlisted prefix simply reads as
 * vendor-unknown; add one only when its owner is certain.
 *
 * The ZTE and Huawei families each have MORE than the one obvious code: this
 * fleet's F670/F670L/F660 estate reports `ZXIC` (ZTE's chipset code), not the
 * `ZTEG` ITU code, and its HG-series carries the odd `HWTT` alongside `HWTC`.
 * Missing `ZXIC` made a whole ZTE estate read as vendor-unknown, so the
 * authorize modal suggested nothing and its list fell to a mangled Nokia row —
 * the F670L bug this table now covers.
 */
export const ONU_VENDOR_CODES: Record<string, string> = {
  // The community-maintained ITU-T vendor-ID registry (hack-gpon.org/vendor),
  // so a serial off almost any manufacturer is recognised rather than read as
  // "a code this platform does not know".
  ADTN: 'Adtran',
  ALCL: 'Nokia',
  ALLG: 'ALLNET',
  ASKY: 'Askey',
  AVMG: 'AVM',
  CDKT: 'KingType',
  CIGG: 'CIG',
  CXNK: 'Calix',
  DDKT: 'DKT',
  DLNK: 'D-Link',
  DSNW: 'DASAN',
  ELTR: 'Electra',
  ELTX: 'Eltex',
  FHTT: 'FiberHome',
  GMTK: 'GemTek',
  GNXS: 'Genexis',
  GPNC: 'NuCom',
  GPON: 'Generic',
  GTHG: 'Nokia',
  HALN: 'HALNy',
  HBMT: 'HiSense',
  HSGQ: 'HSGQ',
  HUMA: 'Humax',
  HWTC: 'Huawei',
  ICTR: 'Icotera',
  ISKT: 'Iskratel',
  KAON: 'Kaon',
  LEOX: 'LEOX',
  LQDE: 'Lantiq',
  MSTC: 'MitraStar',
  NOKG: 'Nokia',
  NOKW: 'Nokia',
  PRLN: 'Prolink',
  PTIN: 'Altice',
  RTKG: 'Realtek',
  SCOM: 'Sercomm',
  SKYW: 'Skyworth',
  SMBS: 'Sagemcom',
  SPGA: 'Source Photonics',
  TMBB: 'Technicolor',
  TPLG: 'TP-Link',
  UBNT: 'Ubiquiti',
  UGRD: 'UGrid',
  YHTC: 'Youhua',
  YOTC: 'YOTC',
  ZNTS: 'DZS',
  ZRMT: 'Zaram',
  ZTEG: 'ZTE',
  ZYWN: 'Zyxel',
  ZYXE: 'Zyxel',
  // Codes NOT in that registry but proven on this fleet or confirmed by the
  // operator — kept because a mapping the network demonstrates outranks a list:
  //   ZXIC  ZTE's chipset code — what most F670/F670L/F660 here actually report,
  //         NOT the ZTEG ITU code (26 of 27 on the fleet). The F670L bug.
  //   HWTT  a Huawei variant on this fleet's HG-series.
  //   RTEG  seen on a ZTE F670L here.
  //   TDTC  Tenda (operator-confirmed).
  //   DBCG  Dasan (pre-existing).
  ZXIC: 'ZTE',
  HWTT: 'Huawei',
  RTEG: 'ZTE',
  TDTC: 'Tenda',
  DBCG: 'Dasan',
  // Budget vendors the operator sees rising in the field but the registry above
  // does not list yet. Kept on the operator's word (2026-09-08). SUMT's vendor
  // is unconfirmed — Sumitomo is the best guess; correct it when a unit names
  // itself. Low risk regardless: the library holds no model for any of these, so
  // the worst a wrong label does is name the vendor wrong in the "pick a model"
  // prompt, never suggest a wrong model.
  BDCM: 'BDCOM',
  CDAT: 'C-Data',
  VSOL: 'VSOL',
  SUMT: 'Sumitomo',
};

/** The vendor a serial declares, or null when its prefix is not one we know. */
export function vendorFromSerial(serial: string): string | null {
  const prefix = serial.trim().slice(0, 4).toUpperCase();
  return ONU_VENDOR_CODES[prefix] ?? null;
}

/**
 * Whether a ZTE chassis's OMCI WiFi-port binding — `vlan port wifi_0/N …` —
 * can land on this ONT.
 *
 * A ZTE ONT carries a WiFi-port entity the OLT binds a VLAN to. A Huawei ONT
 * does not: its radios are the router's, and the SSID reaches a VLAN through
 * a bridged WAN connection ON THE ROUTER. gachie1 answered every one of the
 * 73 Huawei units in the 2026-09-29 Public WiFi run "unknown ME instance" or
 * "command processing error", and the nightly drift check then expected the
 * line from 70 of them for a week. True for a ZTE ONT and for one whose
 * vendor the serial does not declare (the same stance as
 * `chassisConfigVerdictApplies`); false for a declared foreign ONT.
 */
export function zteOmciWifiApplies(serial: string): boolean {
  const vendor = vendorFromSerial(serial);
  return vendor === null || vendor === 'ZTE';
}

/**
 * Whether a ZTE chassis's "Config state" is a verdict on THIS ONU's service,
 * or only on the chassis's own extras.
 *
 * The operator's question (2026-09-21, Carina General Merchandise, Lagos):
 * "if the router is configured and working, why show config failed". The
 * C650's own `show gpon onu config-fail` answered it — every failed item
 * was a ZTE-proprietary managed entity (65321, 65283, 65305, and a UNI
 * attribute set by the ONU-type template at ranging) that the Huawei ONT
 * returned "unknown managed entity" to. None came from the service config;
 * the PPPoE WAN was up and the LAN bound. And it is not one unit: every
 * Huawei on Lagos reads warning or fail (88 of 88), every Huawei on gachie1
 * reads fail (109 of 109), every ZTE ONU on either reads success, and the
 * SmartOLT-built neighbours carry the identical items. A verdict that is
 * constant for a whole vendor is not a verdict on any subscriber.
 *
 * So: on a ZTE chassis the state is reported for a ZTE ONU (its OMCI
 * config IS its service, and `fail` there is real) and for a unit whose
 * vendor the serial does not declare; for a foreign ONT it is withheld —
 * the CPE's own WAN state says whether that subscriber is configured. A
 * Huawei chassis keeps its verdict for every ONT: its `Config state` is
 * about the line/service profiles, and nothing live has shown it to be
 * noise.
 */
export function chassisConfigVerdictApplies(
  oltVendor: string | null | undefined,
  serial: string | null | undefined,
): boolean {
  if (oltVendor !== 'zte') return true;
  const vendor = vendorFromSerial(serial ?? '');
  return vendor === null || vendor === 'ZTE';
}

export interface OnuTypeCandidate {
  id: string;
  name: string;
  vendor: string | null;
  /** How many ONUs on the network already use it. Breaks ties, never shown. */
  onuCount?: number;
}

export interface OnuTypeSuggestion {
  match: OnuTypeCandidate | null;
  /** Why, in a form an operator can read on the screen. */
  reason: string;
  /** True when the guess is good enough to pre-select rather than merely offer. */
  confident: boolean;
}

/**
 * Suggests the model for a serial.
 *
 * Vendor agreement is what carries the weight — a Huawei serial cannot be a ZTE
 * ONU, whatever the names look like. Within one vendor the tie is broken by how
 * many of that model the network already runs, because an operator's next ONU
 * is overwhelmingly the same as their last thousand.
 *
 * A single candidate for the vendor is confident. Several are offered with the
 * most common pre-selected but flagged, so the operator looks. No vendor match
 * at all suggests nothing: guessing across vendors would be worse than asking.
 */
/** Loose model-name key so "HG8546M", "hg8546-m" and "HG8546 M" all match. */
function modelKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function suggestOnuType(
  serial: string,
  candidates: OnuTypeCandidate[],
  /**
   * The exact model the CHASSIS reported, when it does (a Huawei
   * `Ont EquipmentID`). A real signal beats any popularity guess, so a library
   * row matching it wins outright — vendor-agnostic, so a ZTE chassis that ever
   * reports a model gets the same treatment.
   */
  detectedModel?: string | null,
): OnuTypeSuggestion {
  if (detectedModel && detectedModel.trim()) {
    const want = modelKey(detectedModel);
    const exact =
      candidates.find((c) => modelKey(c.name) === want) ??
      candidates.find((c) => {
        const k = modelKey(c.name);
        return k.length >= 4 && (k.includes(want) || want.includes(k));
      });
    if (exact) {
      return {
        match: exact,
        reason: `The chassis reports this ONU as ${detectedModel.trim()}.`,
        confident: true,
      };
    }
    // The chassis named a model the library does not hold yet — fall through to
    // the serial guess, but say what the chassis called it so the operator can
    // add or type it.
  }

  const vendor = vendorFromSerial(serial);
  if (!vendor) {
    return {
      match: null,
      reason: `"${serial.slice(0, 4)}" is not a vendor code this platform knows — pick the model.`,
      confident: false,
    };
  }

  const sameVendor = candidates.filter(
    (c) => c.vendor && c.vendor.toLowerCase() === vendor.toLowerCase(),
  );
  if (sameVendor.length === 0) {
    return {
      match: null,
      reason: `The serial says ${vendor}, and the library holds no ${vendor} model yet — pick or type one.`,
      confident: false,
    };
  }

  const ranked = [...sameVendor].sort(
    (a, b) => (b.onuCount ?? 0) - (a.onuCount ?? 0) || a.name.localeCompare(b.name),
  );
  const best = ranked[0]!;

  if (ranked.length === 1) {
    return {
      match: best,
      reason: `The serial says ${vendor}, and ${best.name} is the only ${vendor} model in the library.`,
      confident: true,
    };
  }

  // One model dominating its vendor is the usual shape of a real deployment,
  // and a clear winner is worth trusting. An even spread is not.
  const runnerUp = ranked[1]!;
  const dominant = (best.onuCount ?? 0) > 0 && (best.onuCount ?? 0) >= (runnerUp.onuCount ?? 0) * 2;
  return {
    match: best,
    reason: dominant
      ? `The serial says ${vendor}, and ${best.name} is what most of your ${vendor} ONUs are.`
      : `The serial says ${vendor}. ${best.name} is the most common, but check it.`,
    confident: dominant,
  };
}
